import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Storefront, ArrowsClockwise, CheckCircle, Package,
  Clock, XCircle, ArrowSquareOut, LinkSimpleBreak, LinkSimple, WhatsappLogo, FilePdf,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import WhatsAppEmojiPicker from "../components/WhatsAppEmojiPicker";
import api from "../lib/api";
import { ExcelTh, useExcelTable } from "../components/ExcelTable";
import ProductoCombobox from "../components/ProductoCombobox";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SyncPreciosTNDialog from "../components/SyncPreciosTNDialog";
import TNPushControl from "../components/TNPushControl";
import NumberInput from "@/components/ui/NumberInput";

const ESTADOS_LOCAL = [
  { value: "sin_confirmar",       label: "Sin Confirmar",       color: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "confirmado",          label: "Confirmado",          color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "en_produccion",       label: "En Producción",       color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "listo_para_facturar", label: "Listo p/ Facturar",   color: "bg-purple-100 text-purple-800 border-purple-300" },
  { value: "a_mostrador",         label: "A Mostrador",         color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  { value: "terminado",           label: "Terminado",           color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "reprogramar",         label: "Reprogramar/Cambio",  color: "bg-rose-100 text-rose-800 border-rose-300" },
  { value: "anulado",             label: "Anulado (NC)",        color: "bg-neutral-200 text-neutral-700 border-neutral-400" },
];

const paymentBadge = (ps) => {
  const map = {
    paid:       { label: "Pagado",       cls: "bg-emerald-50 text-emerald-700" },
    authorized: { label: "Autorizado",   cls: "bg-emerald-50 text-emerald-700" },
    pending:    { label: "Pendiente",    cls: "bg-amber-50 text-amber-700" },
    cancelled:  { label: "Cancelado",    cls: "bg-red-50 text-red-700" },
    refunded:   { label: "Reembolsado",  cls: "bg-neutral-100 text-neutral-700" },
    voided:     { label: "Anulado",      cls: "bg-red-50 text-red-700" },
  };
  return map[ps] || { label: ps || "—", cls: "bg-neutral-100 text-neutral-700" };
};

const fmtMoney = (n, moneda = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: moneda }).format(n || 0);

// Formatea el timestamp completo de TN a "HH:mm" (hora local Argentina)
const fmtHora = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "America/Argentina/Buenos_Aires",
    });
  } catch { return ""; }
};

// Formatea fecha + hora completa "DD/MM/YYYY HH:mm"
const fmtFechaHora = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("es-AR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
      timeZone: "America/Argentina/Buenos_Aires",
    });
  } catch { return ""; }
};

// Normaliza teléfono para link de WhatsApp (formato AR: 549 + área + número, mobile)
const waLink = (phone, message) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return null;
  let normalized = digits;
  if (digits.startsWith("54") && !digits.startsWith("549")) {
    normalized = "549" + digits.slice(2);
  } else if (!digits.startsWith("54") && digits.length >= 10) {
    normalized = "549" + digits;
  }
  const base = `https://wa.me/${normalized}`;
  if (message && message.trim()) {
    return `${base}?text=${encodeURIComponent(message)}`;
  }
  return base;
};

const fmtPhone = (phone) => {
  if (!phone) return "";
  const d = String(phone).replace(/\D/g, "");
  // Formato visual: +54 11 6165-8444 aprox
  if (d.startsWith("54") && d.length >= 12) {
    const area = d.slice(2, 4);
    const rest = d.slice(4);
    return `+54 ${area} ${rest.slice(0, 4)}-${rest.slice(4, 8)}`;
  }
  return String(phone);
};

// Badge para tipo de entrega
const entregaInfo = (v) => {
  const t = v?.entrega_tipo;
  if (t === "pickup") return { label: "Retiro en local", icon: "🏪", cls: "bg-blue-50 text-blue-700 border-blue-200" };
  if (t === "ship") return { label: "Envío a domicilio", icon: "🚚", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  return { label: "—", icon: "❓", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
};

export default function TiendaNubePage() {
  const [status, setStatus] = useState(null);
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterPayment, setFilterPayment] = useState("todos");
  const [search, setSearch] = useState("");
  const [detailVenta, setDetailVenta] = useState(null);
  const [changingId, setChangingId] = useState(null);
  // Edición local de ítems
  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editEnvio, setEditEnvio] = useState(0);
  const [editDescuento, setEditDescuento] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);
  // Catálogo + lista de precios activa para el editor de ítems (misma lógica que Pedidos).
  const [productos, setProductos] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [editListaPreciosId, setEditListaPreciosId] = useState("");
  // Edición de dirección de entrega (para corregir errores o cambios de destino)
  const [editDirMode, setEditDirMode] = useState(false);
  const [editDir, setEditDir] = useState({
    direccion_entrega: "", name: "", phone: "", floor: "", locality: "", zipcode: "", country: "",
    direccion_piso: "", direccion_dpto: "", direccion_timbre: "",
  });
  const [savingDir, setSavingDir] = useState(false);
  // lat/lng resueltos por autocomplete (para actualizar la ruta sin re-geocodear)
  const [editDirLatLng, setEditDirLatLng] = useState({ lat: null, lng: null });

  // Carga inicial de catálogos usados en el edit-mode.
  useEffect(() => {
    api.get("/productos").then(({ data }) => setProductos(data || [])).catch(() => {});
    api.get("/listas-precios").then(({ data }) => setListasPrecios(data || [])).catch(() => {});
  }, []);

  // Al entrar en edit-mode, si no hay lista elegida, defaulteamos a la lista "Minorista"
  // (o cae en la primera disponible).
  useEffect(() => {
    if (!editMode || editListaPreciosId || listasPrecios.length === 0) return;
    const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const minorista = listasPrecios.find((l) => norm(l.nombre).includes("minorista"));
    setEditListaPreciosId(minorista?.id || listasPrecios[0].id);
  }, [editMode, editListaPreciosId, listasPrecios]);
  // WhatsApp templates
  const [waTpl, setWaTpl] = useState({ envio: "", retiro: "" });
  const [waEditOpen, setWaEditOpen] = useState(false);
  const [waEditForm, setWaEditForm] = useState({ envio: "", retiro: "" });
  const [waDefaults, setWaDefaults] = useState({ envio: "", retiro: "" });
  const [savingWa, setSavingWa] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const loadWaTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/tiendanube/wa-templates");
      setWaTpl({ envio: data.envio || "", retiro: data.retiro || "" });
      setWaDefaults(data.defaults || { envio: "", retiro: "" });
    } catch {}
  }, []);
  useEffect(() => { loadWaTemplates(); }, [loadWaTemplates]);

  const openEditWa = () => {
    setWaEditForm({ envio: waTpl.envio, retiro: waTpl.retiro });
    setWaEditOpen(true);
  };
  const saveWaTemplates = async () => {
    setSavingWa(true);
    try {
      const { data } = await api.put("/tiendanube/wa-templates", waEditForm);
      setWaTpl({ envio: data.envio, retiro: data.retiro });
      toast.success("Mensajes actualizados");
      setWaEditOpen(false);
    } catch (e) {
      toast.error("Error al guardar los mensajes");
    } finally {
      setSavingWa(false);
    }
  };
  const resetWaField = (k) => setWaEditForm((f) => ({ ...f, [k]: waDefaults[k] || "" }));

  // Refs a los textareas para poder insertar emojis en la posición del cursor
  const waEnvioTextareaRef = useRef(null);
  const waRetiroTextareaRef = useRef(null);

  const insertEmojiInto = (fieldKey, emoji) => {
    const ref = fieldKey === "envio" ? waEnvioTextareaRef : waRetiroTextareaRef;
    const el = ref.current;
    const current = waEditForm[fieldKey] || "";
    // Si el textarea todavía no está montado, agregamos al final
    if (!el) {
      setWaEditForm((f) => ({ ...f, [fieldKey]: (f[fieldKey] || "") + emoji }));
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const nuevo = current.slice(0, start) + emoji + current.slice(end);
    setWaEditForm((f) => ({ ...f, [fieldKey]: nuevo }));
    // Reposicionar cursor después del emoji insertado
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      } catch { /* noop */ }
    });
  };

  // Devuelve el link de WhatsApp con el mensaje adecuado según tipo de entrega
  const waLinkForVenta = (v) => {
    if (!v?.cliente_telefono) return null;
    const tpl = v.entrega_tipo === "pickup" ? waTpl.retiro : waTpl.envio;
    return waLink(v.cliente_telefono, tpl);
  };

  // ── Edición local de ítems ─────────────────────────────────────────
  const puedeEditarVenta = (v) => v && v.estado_local !== "terminado" && !v.anulada_por_nc_numero;

  // Modo edición de dirección de entrega
  const enterEditDirMode = () => {
    const d = detailVenta?.direccion_entrega_detalle || {};
    setEditDir({
      direccion_entrega: detailVenta?.direccion_entrega || "",
      name: d.name || "",
      phone: d.phone || "",
      floor: d.floor || "",
      locality: d.locality || "",
      zipcode: d.zipcode || "",
      country: d.country || "Argentina",
      direccion_piso: detailVenta?.direccion_piso || "",
      direccion_dpto: detailVenta?.direccion_dpto || "",
      direccion_timbre: detailVenta?.direccion_timbre || "",
    });
    setEditDirLatLng({ lat: null, lng: null });
    setEditDirMode(true);
  };
  const cancelEditDirMode = () => { setEditDirMode(false); };
  const saveEditDir = async () => {
    if (!detailVenta?.id) return;
    setSavingDir(true);
    try {
      // Adjuntamos lat/lng si el usuario eligió del autocomplete (así la ruta
      // se recalcula al instante sin depender de un nuevo geocoding).
      const payload = { ...editDir };
      if (editDirLatLng.lat != null && editDirLatLng.lng != null) {
        payload.lat = editDirLatLng.lat;
        payload.lng = editDirLatLng.lng;
      }
      const { data } = await api.patch(`/tiendanube/ventas/${detailVenta.id}/direccion`, payload);
      setDetailVenta(data);
      setVentas((arr) => arr.map((v) => (v.id === data.id ? { ...v, ...data } : v)));
      toast.success("Dirección de entrega actualizada");
      setEditDirMode(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar la dirección");
    } finally {
      setSavingDir(false);
    }
  };

  const enterEditMode = () => {
    if (!detailVenta) return;
    setEditItems((detailVenta.items || []).map((it) => ({
      tn_product_id: it.tn_product_id || null,
      sku: it.sku || "",
      name: it.name || "",
      quantity: Number(it.quantity || 0),
      price: Number(it.price || 0),
      especificacion: it.especificacion || "",
    })));
    setEditEnvio(Number(detailVenta.envio || 0));
    setEditDescuento(Number(detailVenta.descuento || 0));
    setEditMode(true);
  };
  const exitEditMode = () => { setEditMode(false); };

  const editSubtotal = editItems.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.price) || 0), 0);
  const editTotal = editSubtotal + (Number(editEnvio) || 0) - (Number(editDescuento) || 0);

  const updateItem = (idx, patch) => {
    setEditItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => setEditItems((arr) => arr.filter((_, i) => i !== idx));
  const addItem = () => setEditItems((arr) => [...arr, { name: "", sku: "", quantity: 1, price: 0, tn_product_id: null, especificacion: "" }]);

  const saveItemsEdit = async () => {
    if (!detailVenta) return;
    const invalid = editItems.some((it) => !it.name || it.name.trim() === "" || Number(it.quantity) <= 0);
    if (invalid) {
      toast.error("Cada ítem necesita nombre y cantidad > 0");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/tiendanube/ventas/${detailVenta.id}/items`, {
        items: editItems.map((it) => ({
          tn_product_id: it.tn_product_id || null,
          sku: it.sku || null,
          name: it.name.trim(),
          quantity: Number(it.quantity) || 0,
          price: Number(it.price) || 0,
          especificacion: (it.especificacion || "").trim() || null,
        })),
        envio: Number(editEnvio) || 0,
        descuento: Number(editDescuento) || 0,
      });
      setDetailVenta(data);
      setVentas((arr) => arr.map((v) => (v.id === data.id ? data : v)));
      toast.success("Pedido actualizado");
      setEditMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la edición");
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Nota de Crédito ─────────────────────────────────────────────
  const [ncTarget, setNcTarget] = useState(null);
  const [ncMotivo, setNcMotivo] = useState("");
  const [ncSubmitting, setNcSubmitting] = useState(false);
  const emitirNC = async () => {
    if (!ncTarget?.factura_id) return;
    setNcSubmitting(true);
    try {
      // Estrategia igual que en FacturasPage:
      // 1) DELETE /facturas/{fid} → si es Remito / no-fiscal, backend auto-genera "NOTA DE CREDITO X"
      //    y responde `{anulada_con_nc: true, ...}`
      // 2) Si backend responde 409 con `requires_nota_credito` (factura A/B/C con CAE) →
      //    POST /facturas/{fid}/nota-credito con motivo, emite NC electrónica en AFIP.
      try {
        const { data } = await api.delete(`/facturas/${ncTarget.factura_id}`);
        if (data?.anulada_con_nc) {
          toast.success(`${data.tipo_comprobante} ${data.nota_credito_numero} generada`);
        } else {
          toast.success("Comprobante anulado");
        }
      } catch (delErr) {
        const status = delErr?.response?.status;
        const detail = delErr?.response?.data?.detail;
        if (status === 409 && detail?.requires_nota_credito) {
          const { data } = await api.post(
            `/ventas/facturas/${ncTarget.factura_id}/nota-credito`,
            { motivo: ncMotivo || undefined, auto_afip: true },
          );
          const nc = data?.nota_credito || {};
          toast.success(`Nota de Crédito ${nc.numero || ""} emitida`);
        } else {
          throw delErr;
        }
      }
      setNcTarget(null);
      setNcMotivo("");
      setDetailVenta(null);
      loadVentas();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "No se pudo emitir la Nota de Crédito"));
    } finally {
      setNcSubmitting(false);
    }
  };

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/tiendanube/status");
      setStatus(data);
    } catch (e) {
      toast.error("No se pudo consultar el estado de Tienda Nube");
    }
  }, []);

  const descargarPDF = async (v) => {
    try {
      const res = await api.get(`/tiendanube/ventas/${v.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el PDF");
    }
  };

  const loadVentas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // No enviamos `estado` al backend — el filtro por estado se aplica en cliente
      // para que las stat cards siempre muestren el total de cada estado.
      if (filterPayment !== "todos") params.payment_status = filterPayment;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/tiendanube/ventas", { params });
      setVentas(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Error al cargar ventas de Tienda Nube");
    } finally {
      setLoading(false);
    }
  }, [filterPayment, search]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.connected) loadVentas(); }, [status?.connected, loadVentas]);

  // Auto-refresh cada 5 min
  useEffect(() => {
    if (!status?.connected) return;
    const id = setInterval(loadVentas, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [status?.connected, loadVentas]);

  // Feedback del callback OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tn_connected")) {
      toast.success("Tienda Nube conectada correctamente");
      navigate("/tienda-nube", { replace: true });
      loadStatus();
    }
    const err = params.get("tn_error");
    if (err) {
      toast.error(`Error al conectar Tienda Nube: ${err}`);
      navigate("/tienda-nube", { replace: true });
    }
  }, [location.search, navigate, loadStatus]);

  const handleConnect = async () => {
    try {
      const { data } = await api.get("/tiendanube/install-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la URL de instalación");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("¿Desconectar Tienda Nube? Las ventas ya sincronizadas se conservan pero dejarás de recibir nuevas hasta reconectar.")) return;
    try {
      await api.post("/tiendanube/disconnect");
      toast.success("Tienda Nube desconectada");
      loadStatus();
    } catch (e) {
      toast.error("Error al desconectar");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/tiendanube/sync");
      toast.success(`Sync completado · ${data.created} nuevas · ${data.updated} actualizadas`);
      loadVentas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const [syncPreciosOpen, setSyncPreciosOpen] = useState(false);
  // Nota: el botón manual para empujar stock → TN vive en la página Stock
  // (hook useSyncStockTN, corre en background con progreso).

  // Guardar la fecha de envío (columna editable)
  // Cambiar el tipo de entrega (pickup ↔ ship). Se usa cuando el cliente
  // eligió una opción por error y hay que corregirla del lado del frigorífico.
  const setEntregaTipo = async (vid, nuevoTipo) => {
    const venta = ventas.find((x) => x.id === vid) || {};
    if (venta.entrega_tipo === nuevoTipo) return;
    const label = nuevoTipo === "ship" ? "Envío a domicilio" : "Retiro en el local";
    if (!window.confirm(`¿Cambiar la entrega de este pedido a "${label}"?`)) return;
    setVentas((arr) => arr.map((x) => (x.id === vid ? { ...x, entrega_tipo: nuevoTipo } : x)));
    try {
      await api.patch(`/tiendanube/ventas/${vid}/entrega-tipo`, { entrega_tipo: nuevoTipo });
      toast.success(`Tipo de entrega actualizado a ${label}`);
    } catch (e) {
      toast.error("No se pudo actualizar el tipo de entrega");
      loadVentas();
    }
  };

  const setFechaEnvio = async (vid, fecha) => {
    // El `<input type="date">` dispara `onChange` en CADA tecla del año:
    // "2" → "0002-...", "20" → "0020-...", etc. Esos valores intermedios
    // caen bajo el mínimo válido y el backend los rechaza con 400. Sólo
    // disparamos el PATCH cuando la fecha está completamente escrita (año ≥ 1000)
    // o cuando el user la vació explícitamente para limpiar.
    const clean = (fecha || "").trim();
    if (clean && !/^\d{4}-\d{2}-\d{2}$/.test(clean)) return;
    if (clean && Number(clean.slice(0, 4)) < 1000) return;
    // Optimistic update
    setVentas((arr) => arr.map((x) => (x.id === vid ? { ...x, fecha_envio: clean || null } : x)));
    try {
      await api.patch(`/tiendanube/ventas/${vid}/fecha-envio`, { fecha_envio: clean || null });
    } catch (e) {
      toast.error("No se pudo guardar la fecha de envío");
      loadVentas();
    }
  };

  // Actualizar rango horario de entrega (desde/hasta) para una venta TN.
  const setHorarioEntrega = async (vid, desde, hasta) => {
    // Los `<input type="time">` también pueden emitir valores parciales
    // durante el tipeo — filtramos si no llega en formato HH:MM completo.
    const validTime = (t) => !t || /^\d{2}:\d{2}$/.test(t);
    if (!validTime(desde) || !validTime(hasta)) return;
    // Optimistic update: conserva la fecha y toca sólo los horarios
    setVentas((arr) => arr.map((x) => x.id === vid ? { ...x, horario_entrega_desde: desde || null, horario_entrega_hasta: hasta || null } : x));
    try {
      const venta = ventas.find((x) => x.id === vid) || {};
      await api.patch(`/tiendanube/ventas/${vid}/fecha-envio`, {
        fecha_envio: venta.fecha_envio || null,
        horario_entrega_desde: (desde || "").trim() || null,
        horario_entrega_hasta: (hasta || "").trim() || null,
      });
    } catch (e) {
      toast.error("No se pudo guardar el horario de entrega");
      loadVentas();
    }
  };

  const cambiarEstado = async (venta, nuevoEstado) => {
    if (venta.estado_local === nuevoEstado) return;
    // Traba UX: no permitir pasar a "en_produccion" sin fecha de envío cargada.
    if (nuevoEstado === "en_produccion" && !(venta.fecha_envio || "").trim()) {
      toast.error(
        "Cargá la fecha de envío antes de mandarlo a producción",
        { description: "Sin fecha el pedido puede perderse en el circuito", duration: 6000 },
      );
      return;
    }
    // "A Mostrador" requiere fecha de envío para que aparezca en la pestaña
    // Delivery del POS (que filtra por fecha del día).
    if (nuevoEstado === "a_mostrador" && !(venta.fecha_envio || "").trim()) {
      toast.error(
        "Cargá la fecha de envío antes de marcarlo A Mostrador",
        { description: "El POS toma los pedidos por fecha para la pestaña Delivery", duration: 6000 },
      );
      return;
    }
    const pushToTN = nuevoEstado === "terminado"
      ? window.confirm(`Marcar la venta #${venta.tn_number} como "Terminado" y también notificar a Tienda Nube como "Enviado"?\n\nSí = Actualiza el ERP + notifica al cliente por email vía Tienda Nube.\nCancelar = Sólo cambia el estado en el ERP.`)
      : false;
    setChangingId(venta.id);
    try {
      const { data } = await api.patch(`/tiendanube/ventas/${venta.id}/estado`, {
        estado: nuevoEstado,
        push_to_tn: pushToTN,
      });
      toast.success(`Estado cambiado a ${nuevoEstado.replace("_", " ")}`);
      if (data.tn_sync && !data.tn_sync.ok) {
        toast.warning("El estado se cambió en el ERP pero Tienda Nube no confirmó el envío. Revisá la solapa manualmente.");
      }
      loadVentas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar estado");
    } finally {
      setChangingId(null);
    }
  };

  const stats = useMemo(() => {
    const total = ventas.length;
    const s1 = ventas.filter(v => v.estado_local === "sin_confirmar").length;
    const s2 = ventas.filter(v => v.estado_local === "confirmado").length;
    const sProd = ventas.filter(v => v.estado_local === "en_produccion").length;
    const sMost = ventas.filter(v => v.estado_local === "a_mostrador").length;
    const sListo = ventas.filter(v => v.estado_local === "listo_para_facturar").length;
    const s3 = ventas.filter(v => v.estado_local === "terminado").length;
    const sReprog = ventas.filter(v => v.estado_local === "reprogramar").length;
    return { total, s1, s2, sProd, sMost, sListo, s3, sReprog };
  }, [ventas]);

  // Filtro aplicado en cliente (para que los stats siempre reflejen el total real)
  const filteredVentas = useMemo(() => {
    if (filterEstado === "todos") return ventas;
    return ventas.filter(v => v.estado_local === filterEstado);
  }, [ventas, filterEstado]);

  // Sort + filtros tipo Excel por columna.
  const excelAccessors = useMemo(() => ({
    numero: (r) => r.tn_number,
    fecha: (r) => r.fecha,
    cliente: (r) => r.cliente_nombre || "",
    telefono: (r) => r.cliente_telefono || "",
    entrega: (r) => entregaInfo(r).label,
    fecha_envio: (r) => r.fecha_envio || "",
    pago: (r) => r.metodo_pago || "",
    total: (r) => Number(r.total) || 0,
    estado_local: (r) => (ESTADOS_LOCAL.find((e) => e.value === r.estado_local) || { label: r.estado_local || "—" }).label,
  }), []);

  const {
    sortedRows: displayVentas, sort, toggleSort,
    filters: excelFilters, setFilter: setExcelFilter, clearFilter: clearExcelFilter,
    uniqueValues: excelUnique,
  } = useExcelTable(filteredVentas, excelAccessors);

  // ---------- Render: no conectado ----------
  if (status && !status.connected) {
    return (
      <div className="p-4 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Storefront size={32} weight="duotone" className="text-[#921A0E]" />
          <div>
            <h1 className="text-2xl font-bold font-heading">Tienda Nube</h1>
            <p className="text-sm text-neutral-600">Sincronización de ventas online con labiferia.com.ar</p>
          </div>
        </div>

        <div className="border border-neutral-300 rounded-lg p-8 bg-white">
          <div className="text-center max-w-lg mx-auto">
            <Storefront size={64} weight="thin" className="mx-auto mb-4 text-neutral-400" />
            <h2 className="text-xl font-semibold mb-2">Conectar tu tienda online</h2>
            <p className="text-sm text-neutral-600 mb-6">
              Al conectar, el ERP va a recibir automáticamente todas las ventas nuevas
              de <strong>labiferia.com.ar</strong> y las vas a poder gestionar desde acá con estados
              <em> Sin Confirmar → Confirmado → Terminado</em>.
            </p>

            {!status.configured && (
              <div className="bg-amber-50 border border-amber-300 rounded p-4 mb-6 text-left">
                <div className="font-semibold text-amber-800 mb-1">⚠ App aún no configurada</div>
                <div className="text-xs text-amber-700 leading-relaxed">
                  El backend todavía no tiene las credenciales de la app de Tienda Nube
                  (App ID, Client ID, Client Secret). Cargalas en <code className="bg-white px-1">/app/backend/.env</code> con los valores del panel de partners.tiendanube.com y reiniciá el backend.
                </div>
              </div>
            )}

            <Button
              data-testid="btn-connect-tiendanube"
              onClick={handleConnect}
              disabled={!status.configured}
              className="bg-[#921A0E] hover:bg-[#7a1608] text-white gap-2"
            >
              <LinkSimple size={18} weight="bold" />
              Conectar Tienda Nube
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Render: conectado ----------
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Storefront size={32} weight="duotone" className="text-[#921A0E]" />
          <div>
            <h1 className="text-2xl font-bold font-heading">Tienda Nube</h1>
            <p className="text-sm text-neutral-600">
              Conectado a <strong>{status?.store_name || `Store #${status?.store_id}`}</strong>
              {" · "}
              <span className="text-[11px] font-mono">
                desde {(status?.connected_at || "").substring(0, 10)}
              </span>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="btn-edit-wa-templates"
            variant="outline"
            onClick={openEditWa}
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <WhatsappLogo size={16} weight="fill" className="text-emerald-600" />
            Editar mensajes
          </Button>
          <Button
            data-testid="btn-sync-precios-tn"
            variant="outline"
            onClick={() => setSyncPreciosOpen(true)}
            className="gap-2 border-sky-300 text-sky-700 hover:bg-sky-50"
            title="Empujar precios de la lista marcada como 'TN' hacia Tienda Nube (con preview)"
          >
            <Storefront size={16} weight="bold" />
            Sincronizar precios
          </Button>
          <Button
            data-testid="btn-sync-tiendanube"
            onClick={handleSync}
            disabled={syncing}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
            {syncing ? "Sincronizando..." : "Sincronizar ventas"}
          </Button>
          <Button
            data-testid="btn-disconnect-tiendanube"
            variant="outline"
            onClick={handleDisconnect}
            className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
          >
            <LinkSimpleBreak size={16} weight="bold" />
            Desconectar
          </Button>
        </div>
      </div>

      <TNPushControl status={status} onChanged={loadStatus} />

      {/* Stats (clickeables como filtros) */}
      <div className="grid grid-cols-3 md:grid-cols-8 gap-3 mb-4">
        <StatCard label="Total" value={stats.total} icon={Package}
          active={filterEstado === "todos"} onClick={() => setFilterEstado("todos")} />
        <StatCard label="Sin Confirmar" value={stats.s1} icon={Clock} colorClass="text-amber-700"
          active={filterEstado === "sin_confirmar"} onClick={() => setFilterEstado("sin_confirmar")} />
        <StatCard label="Confirmadas" value={stats.s2} icon={CheckCircle} colorClass="text-blue-700"
          active={filterEstado === "confirmado"} onClick={() => setFilterEstado("confirmado")} />
        <StatCard label="En Producción" value={stats.sProd} icon={Clock} colorClass="text-orange-700"
          active={filterEstado === "en_produccion"} onClick={() => setFilterEstado("en_produccion")} />
        <StatCard label="En Mostrador" value={stats.sMost} icon={Storefront} colorClass="text-fuchsia-700"
          active={filterEstado === "a_mostrador"} onClick={() => setFilterEstado("a_mostrador")} />
        <StatCard label="Listo p/ Facturar" value={stats.sListo} icon={CheckCircle} colorClass="text-purple-700"
          active={filterEstado === "listo_para_facturar"} onClick={() => setFilterEstado("listo_para_facturar")} />
        <StatCard label="Terminadas" value={stats.s3} icon={CheckCircle} colorClass="text-emerald-700"
          active={filterEstado === "terminado"} onClick={() => setFilterEstado("terminado")} />
        <StatCard label="Reprogramar/Cambio" value={stats.sReprog} icon={Clock} colorClass="text-rose-700"
          active={filterEstado === "reprogramar"} onClick={() => setFilterEstado("reprogramar")} />
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-3 items-center">
        <Input
          data-testid="tiendanube-search"
          placeholder="Buscar por cliente, email o Nº"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadVentas()}
          className="max-w-xs"
        />
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="w-[180px]" data-testid="filter-estado-local">
            <SelectValue placeholder="Estado ERP" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {ESTADOS_LOCAL.map((e) => (
              <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPayment} onValueChange={setFilterPayment}>
          <SelectTrigger className="w-[180px]" data-testid="filter-payment">
            <SelectValue placeholder="Pago" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los pagos</SelectItem>
            <SelectItem value="paid">Pagados</SelectItem>
            <SelectItem value="authorized">Autorizados</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabla */}
      <div className="border border-neutral-300 rounded bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-300">
              <tr className="text-xs uppercase text-neutral-500 font-mono">
                <ExcelTh col="numero" label="Nº" testId="tn-th-numero"
                  sortDir={sort.col === "numero" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("numero")} selected={excelFilters.numero}
                  onFilterChange={(s) => setExcelFilter("numero", s)}
                  onClearFilter={() => clearExcelFilter("numero")} />
                <ExcelTh col="fecha" label="Fecha" testId="tn-th-fecha"
                  sortDir={sort.col === "fecha" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("fecha")} selected={excelFilters.fecha}
                  onFilterChange={(s) => setExcelFilter("fecha", s)}
                  onClearFilter={() => clearExcelFilter("fecha")} />
                <ExcelTh col="cliente" label="Cliente" testId="tn-th-cliente"
                  sortDir={sort.col === "cliente" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("cliente")} selected={excelFilters.cliente}
                  onFilterChange={(s) => setExcelFilter("cliente", s)}
                  onClearFilter={() => clearExcelFilter("cliente")} />
                <ExcelTh col="telefono" label="Teléfono" className="w-44" testId="tn-th-telefono"
                  sortDir={sort.col === "telefono" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("telefono")} selected={excelFilters.telefono}
                  onFilterChange={(s) => setExcelFilter("telefono", s)}
                  onClearFilter={() => clearExcelFilter("telefono")} />
                <ExcelTh col="entrega" label="Entrega" className="w-40" testId="tn-th-entrega"
                  sortDir={sort.col === "entrega" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("entrega")} selected={excelFilters.entrega}
                  onFilterChange={(s) => setExcelFilter("entrega", s)}
                  onClearFilter={() => clearExcelFilter("entrega")} />
                <ExcelTh col="fecha_envio" label="Fecha envío" className="w-36" testId="tn-th-fecha-envio"
                  sortDir={sort.col === "fecha_envio" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("fecha_envio")} selected={excelFilters.fecha_envio}
                  onFilterChange={(s) => setExcelFilter("fecha_envio", s)}
                  onClearFilter={() => clearExcelFilter("fecha_envio")} />
                <ExcelTh col="pago" label="Pago" testId="tn-th-pago"
                  sortDir={sort.col === "pago" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("pago")} selected={excelFilters.pago}
                  onFilterChange={(s) => setExcelFilter("pago", s)}
                  onClearFilter={() => clearExcelFilter("pago")} />
                <ExcelTh col="total" label="Total" align="right" testId="tn-th-total"
                  sortDir={sort.col === "total" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("total")} selected={excelFilters.total}
                  onFilterChange={(s) => setExcelFilter("total", s)}
                  onClearFilter={() => clearExcelFilter("total")} />
                <ExcelTh col="estado_local" label="Estado ERP" align="center" testId="tn-th-estado"
                  sortDir={sort.col === "estado_local" ? sort.dir : null} onSort={toggleSort}
                  options={excelUnique("estado_local")} selected={excelFilters.estado_local}
                  onFilterChange={(s) => setExcelFilter("estado_local", s)}
                  onClearFilter={() => clearExcelFilter("estado_local")} />
                <th className="text-center px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && displayVentas.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-neutral-500">Cargando...</td></tr>
              )}
              {!loading && displayVentas.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-neutral-500">
                  {ventas.length === 0
                    ? <>Sin ventas. Hacé click en <strong>Sincronizar ahora</strong> para traer las ventas desde Tienda Nube.</>
                    : <>Sin resultados para este filtro.</>}
                </td></tr>
              )}
              {displayVentas.map((v) => {
                const est = ESTADOS_LOCAL.find(e => e.value === v.estado_local) || ESTADOS_LOCAL[0];
                const pay = paymentBadge(v.payment_status);
                const wa = waLinkForVenta(v);
                return (
                  <tr key={v.id} className="border-b border-neutral-200 hover:bg-neutral-50" data-testid={`tn-venta-row-${v.tn_number}`}>
                    <td className="px-3 py-2 font-mono font-bold">#{v.tn_number}</td>
                    <td className="px-3 py-2 font-mono text-xs" data-testid={`tn-fecha-${v.tn_number}`}>
                      <div>{v.fecha}</div>
                      {v.tn_created_at && (
                        <div className="text-[10px] text-neutral-500">{fmtHora(v.tn_created_at)} hs</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.cliente_nombre || "—"}</div>
                      {v.cliente_email && <div className="text-xs text-neutral-500">{v.cliente_email}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {v.cliente_telefono ? (
                        wa ? (
                          <a
                            href={wa}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[13px] text-emerald-700 hover:text-emerald-800 hover:underline font-mono"
                            title="Abrir chat en WhatsApp"
                            data-testid={`tn-wa-${v.tn_number}`}
                          >
                            <WhatsappLogo size={14} weight="fill" className="text-emerald-600" />
                            {fmtPhone(v.cliente_telefono)}
                          </a>
                        ) : (
                          <span className="font-mono text-[13px] text-neutral-600">{fmtPhone(v.cliente_telefono)}</span>
                        )
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {(() => {
                        const ei = entregaInfo(v);
                        const alterna = v.entrega_tipo === "ship" ? "pickup" : "ship";
                        return (
                          <div className="flex flex-col gap-0.5">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium w-fit ${ei.cls}`}>
                              {ei.icon} {ei.label}
                            </span>
                            {v.entrega_opcion && v.entrega_tipo === "ship" && (
                              <span className="text-[10px] text-neutral-500 font-mono truncate max-w-[160px]" title={v.entrega_opcion}>
                                {v.entrega_opcion}
                              </span>
                            )}
                            {v.entrega_sucursal && v.entrega_tipo === "pickup" && (
                              <span className="text-[10px] text-neutral-500 font-mono truncate max-w-[160px]" title={v.entrega_sucursal}>
                                📍 {v.entrega_sucursal}
                              </span>
                            )}
                            <button
                              onClick={() => setEntregaTipo(v.id, alterna)}
                              className="text-[10px] font-mono uppercase text-emerald-700 hover:text-emerald-900 hover:underline w-fit mt-0.5"
                              data-testid={`tn-cambiar-entrega-${v.tn_number}`}
                              title="El cliente eligió mal y hay que corregirlo"
                            >
                              → cambiar a {alterna === "ship" ? "envío" : "retiro"}
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <DateInput
                        value={v.fecha_envio || ""}
                        min={(v.tn_created_at || v.fecha || "").slice(0, 10) || undefined}
                        onChange={(e) => setFechaEnvio(v.id, e.target.value)}
                        className="text-xs font-mono border border-neutral-300 rounded px-1.5 py-1 w-full focus:border-emerald-500"
                        data-testid={`tn-fecha-envio-${v.tn_number}`}
                      />
                      <div className="flex gap-1 mt-1">
                        <input
                          type="time"
                          value={v.horario_entrega_desde || ""}
                          onChange={(e) => setHorarioEntrega(v.id, e.target.value, v.horario_entrega_hasta || "")}
                          placeholder="desde"
                          title="Desde"
                          className="text-[10px] font-mono border border-neutral-200 rounded px-1 py-0.5 flex-1 min-w-0 focus:border-emerald-500"
                          data-testid={`tn-hora-desde-${v.tn_number}`}
                        />
                        <input
                          type="time"
                          value={v.horario_entrega_hasta || ""}
                          onChange={(e) => setHorarioEntrega(v.id, v.horario_entrega_desde || "", e.target.value)}
                          placeholder="hasta"
                          title="Hasta"
                          className="text-[10px] font-mono border border-neutral-200 rounded px-1 py-0.5 flex-1 min-w-0 focus:border-emerald-500"
                          data-testid={`tn-hora-hasta-${v.tn_number}`}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        <Badge variant="outline" className={`${pay.cls} text-xs font-normal w-fit`}>{pay.label}</Badge>
                        {v.metodo_pago && (
                          <span className="text-[10px] text-neutral-500 font-mono truncate max-w-[140px]" title={v.metodo_pago}>
                            {v.metodo_pago}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(v.total, v.moneda)}</td>
                    <td className="px-3 py-2 text-center">
                      <Select
                        value={v.estado_local}
                        onValueChange={(nv) => cambiarEstado(v, nv)}
                        disabled={changingId === v.id || v.estado_local === "anulado"}
                      >
                        <SelectTrigger
                          data-testid={`tn-estado-selector-${v.tn_number}`}
                          className={`w-[150px] mx-auto text-xs h-8 border ${est.color} ${v.estado_local === "anulado" ? "opacity-70 cursor-not-allowed" : ""}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS_LOCAL.map((e) => (
                            <SelectItem key={e.value} value={e.value} disabled={e.value === "anulado"}>{e.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {v.anulada_por_nc_numero ? (
                          <Badge
                            variant="outline"
                            className="bg-neutral-200 text-neutral-700 border-neutral-400 text-[10px]"
                            data-testid={`tn-nc-badge-${v.tn_number}`}
                            title={`Anulada con NC ${v.anulada_por_nc_numero}`}
                          >
                            ⊘ NC {v.anulada_por_nc_numero}
                          </Badge>
                        ) : v.factura_id ? (
                          <Badge
                            variant="outline"
                            className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
                            data-testid={`tn-factura-badge-${v.tn_number}`}
                            title="Factura vinculada"
                          >
                            ✓ Fact. {v.factura_numero || ""}
                          </Badge>
                        ) : (
                          <Button
                            data-testid={`tn-facturar-${v.tn_number}`}
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/ventas?tn_venta_id=${v.id}`)}
                            className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            title="Pre-cargar venta y facturar"
                          >
                            Facturar
                          </Button>
                        )}
                        <Button
                          data-testid={`tn-ver-detalle-${v.tn_number}`}
                          size="sm"
                          variant="ghost"
                          onClick={() => setDetailVenta(v)}
                          className="h-6 text-[11px] gap-1"
                        >
                          <ArrowSquareOut size={12} /> Ver
                        </Button>
                        <Button
                          data-testid={`tn-pdf-${v.tn_number}`}
                          size="sm"
                          variant="ghost"
                          onClick={() => descargarPDF(v)}
                          className="h-6 px-1.5 text-slate-700"
                          title="Descargar PDF"
                        >
                          <FilePdf size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detalle */}
      <Dialog open={!!detailVenta} onOpenChange={(o) => { if (!o) { setDetailVenta(null); setEditMode(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Detalle venta #{detailVenta?.tn_number}</DialogTitle>
          </DialogHeader>
          {detailVenta && (
            <div className="space-y-4 text-sm flex-1 overflow-y-auto pr-1 -mr-1">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fecha y hora" value={detailVenta.tn_created_at ? fmtFechaHora(detailVenta.tn_created_at) : detailVenta.fecha} mono />
                <Field label="Total" value={fmtMoney(detailVenta.total, detailVenta.moneda)} mono />
                <Field label="Cliente" value={detailVenta.cliente_nombre} />
                <Field label="Email" value={detailVenta.cliente_email} />
                <Field label="CUIT/DNI" value={detailVenta.cliente_cuit} />
                <div>
                  <div className="text-xs uppercase text-neutral-500 font-mono">Teléfono</div>
                  {detailVenta.cliente_telefono ? (
                    <a
                      href={waLinkForVenta(detailVenta)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-800 hover:underline font-mono text-sm mt-0.5"
                      data-testid="tn-detalle-wa"
                    >
                      <WhatsappLogo size={14} weight="fill" className="text-emerald-600" />
                      {fmtPhone(detailVenta.cliente_telefono)}
                    </a>
                  ) : (
                    <div className="text-sm text-neutral-400 mt-0.5">—</div>
                  )}
                </div>
                <Field label="Envío" value={detailVenta.shipping_status} />
              </div>

              {/* Método de pago */}
              <div className="border border-emerald-200 rounded p-3 bg-emerald-50/40" data-testid="tn-metodo-pago">
                <div className="text-xs uppercase text-emerald-700 font-mono mb-2 font-bold flex items-center gap-1">
                  Método de pago
                  <Badge variant="outline" className={`${paymentBadge(detailVenta.payment_status).cls} text-[10px] ml-2`}>
                    {paymentBadge(detailVenta.payment_status).label}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div>
                    <span className="text-neutral-500 text-xs">Medio:</span>{" "}
                    <span className="font-medium text-neutral-800">{detailVenta.metodo_pago || "—"}</span>
                  </div>
                  {detailVenta.pago_installments > 1 && (
                    <div>
                      <span className="text-neutral-500 text-xs">Cuotas:</span>{" "}
                      <span className="font-mono">{detailVenta.pago_installments}</span>
                    </div>
                  )}
                  {detailVenta.metodo_pago_tipo && (
                    <div>
                      <span className="text-neutral-500 text-xs">Gateway:</span>{" "}
                      <span className="font-mono text-xs text-neutral-600 uppercase">{detailVenta.metodo_pago_tipo}</span>
                    </div>
                  )}
                  {detailVenta.pago_paid_at && (
                    <div>
                      <span className="text-neutral-500 text-xs">Pagado el:</span>{" "}
                      <span className="font-mono text-xs">{new Date(detailVenta.pago_paid_at).toLocaleString("es-AR")}</span>
                    </div>
                  )}
                </div>
                {detailVenta.payment_status === "pending" && (
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                    ⚠ El cliente eligió <strong>{detailVenta.metodo_pago || "un medio offline"}</strong> — coordiná el cobro al retirar/entregar.
                  </div>
                )}
              </div>

              {/* Tipo de entrega + dirección/sucursal */}
              {(() => {
                const ei = entregaInfo(detailVenta);
                const esPickup = detailVenta.entrega_tipo === "pickup";
                return (
                  <div className={`border rounded p-3 ${esPickup ? "border-blue-200 bg-blue-50/40" : "border-emerald-200 bg-emerald-50/30"}`} data-testid="tn-entrega-block">
                    <div className={`text-xs uppercase font-mono mb-2 font-bold flex items-center gap-2 ${esPickup ? "text-blue-700" : "text-emerald-700"}`}>
                      Entrega
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] normal-case font-medium ${ei.cls}`}>
                        {ei.icon} {ei.label}
                      </span>
                    </div>

                    {esPickup ? (
                      <div className="space-y-1 text-sm">
                        {detailVenta.entrega_sucursal ? (
                          <div className="text-neutral-800">
                            <span className="text-neutral-500 text-xs">Sucursal:</span>{" "}
                            <strong>📍 {detailVenta.entrega_sucursal}</strong>
                          </div>
                        ) : (
                          <div className="text-neutral-800">El cliente eligió retirar en el local.</div>
                        )}
                        {detailVenta.entrega_opcion && (
                          <div className="text-neutral-600 text-xs font-mono">
                            {detailVenta.entrega_opcion}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        {detailVenta.entrega_opcion && (
                          <div className="text-neutral-800">
                            <span className="text-neutral-500 text-xs">Servicio:</span>{" "}
                            <strong>🚚 {detailVenta.entrega_opcion}</strong>
                            {detailVenta.entrega_costo != null && (
                              <span className="text-neutral-500 text-xs ml-2">
                                ({fmtMoney(detailVenta.entrega_costo, detailVenta.moneda)})
                              </span>
                            )}
                          </div>
                        )}
                        {editDirMode ? (
                          <div className="mt-2 space-y-2 border border-amber-300 bg-amber-50/60 rounded p-3">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 font-bold">
                              Editar dirección de entrega
                            </div>
                            <AddressAutocomplete
                              value={editDir.direccion_entrega}
                              onChange={(v) => setEditDir((d) => ({ ...d, direccion_entrega: v }))}
                              onSelect={(p) => {
                                // Al elegir del desplegable, autocompletamos calle+altura,
                                // localidad, CP y lat/lng para que la ruta se recalcule sin
                                // depender de un nuevo geocoding.
                                const calle = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ");
                                setEditDir((d) => ({
                                  ...d,
                                  direccion_entrega: calle || p.display_name || d.direccion_entrega,
                                  locality: p.direccion_localidad || d.locality,
                                  zipcode: p.direccion_cp || d.zipcode,
                                  country: p.direccion_provincia ? d.country : d.country,
                                }));
                                setEditDirLatLng({ lat: p.lat, lng: p.lon });
                              }}
                              placeholder="Buscá la dirección (calle, número, localidad)…"
                              testId="tn-edit-dir-autocomplete"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="A nombre de"
                                value={editDir.name}
                                onChange={(e) => setEditDir((d) => ({ ...d, name: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-name"
                              />
                              <Input
                                placeholder="Teléfono"
                                value={editDir.phone}
                                onChange={(e) => setEditDir((d) => ({ ...d, phone: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-phone"
                              />
                              <Input
                                placeholder="Piso/Dpto"
                                value={editDir.floor}
                                onChange={(e) => setEditDir((d) => ({ ...d, floor: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-floor"
                              />
                              <Input
                                placeholder="Barrio / Localidad"
                                value={editDir.locality}
                                onChange={(e) => setEditDir((d) => ({ ...d, locality: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-locality"
                              />
                              <Input
                                placeholder="CP"
                                value={editDir.zipcode}
                                onChange={(e) => setEditDir((d) => ({ ...d, zipcode: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-zipcode"
                              />
                              <Input
                                placeholder="País"
                                value={editDir.country}
                                onChange={(e) => setEditDir((d) => ({ ...d, country: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-country"
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <Input
                                placeholder="Piso (ej: 3)"
                                value={editDir.direccion_piso}
                                onChange={(e) => setEditDir((d) => ({ ...d, direccion_piso: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-piso"
                              />
                              <Input
                                placeholder="Dpto (ej: B)"
                                value={editDir.direccion_dpto}
                                onChange={(e) => setEditDir((d) => ({ ...d, direccion_dpto: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-dpto"
                              />
                              <Input
                                placeholder="Timbre"
                                value={editDir.direccion_timbre}
                                onChange={(e) => setEditDir((d) => ({ ...d, direccion_timbre: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid="tn-edit-dir-timbre"
                              />
                            </div>
                            <div className="text-[10px] text-amber-800/80">
                              Al guardar, se recalcula el geocoding para logística.
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEditDirMode} disabled={savingDir}>
                                Cancelar
                              </Button>
                              <Button size="sm" className="h-7 text-xs" onClick={saveEditDir} disabled={savingDir} data-testid="tn-edit-dir-save">
                                {savingDir ? "Guardando…" : "Guardar dirección"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {detailVenta.direccion_entrega && (
                              <div className="text-neutral-800 text-sm">{detailVenta.direccion_entrega}</div>
                            )}
                            {detailVenta.direccion_entrega_detalle && (
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-neutral-600">
                                {detailVenta.direccion_entrega_detalle.name && (
                                  <div><span className="text-neutral-400">A nombre de:</span> {detailVenta.direccion_entrega_detalle.name}</div>
                                )}
                                {detailVenta.direccion_entrega_detalle.phone && (
                                  <div><span className="text-neutral-400">Tel:</span> {detailVenta.direccion_entrega_detalle.phone}</div>
                                )}
                                {detailVenta.direccion_entrega_detalle.floor && (
                                  <div><span className="text-neutral-400">Piso/Dpto:</span> {detailVenta.direccion_entrega_detalle.floor}</div>
                                )}
                                {detailVenta.direccion_entrega_detalle.locality && (
                                  <div><span className="text-neutral-400">Barrio:</span> {detailVenta.direccion_entrega_detalle.locality}</div>
                                )}
                                {detailVenta.direccion_entrega_detalle.zipcode && (
                                  <div><span className="text-neutral-400">CP:</span> {detailVenta.direccion_entrega_detalle.zipcode}</div>
                                )}
                                {detailVenta.direccion_entrega_detalle.country && (
                                  <div><span className="text-neutral-400">País:</span> {detailVenta.direccion_entrega_detalle.country}</div>
                                )}
                              </div>
                            )}
                            {detailVenta.direccion_editada_manual && (
                              <div className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block">
                                ✎ Editada manualmente
                              </div>
                            )}
                            {puedeEditarVenta(detailVenta) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs mt-1"
                                onClick={enterEditDirMode}
                                data-testid="tn-edit-dir-btn"
                              >
                                ✎ Editar dirección
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase text-neutral-500 font-mono">Ítems</div>
                  {puedeEditarVenta(detailVenta) && !editMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={enterEditMode}
                      className="h-7 px-2 text-xs"
                      data-testid="tn-editar-items-btn"
                    >
                      ✎ Editar ítems
                    </Button>
                  )}
                  {editMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addItem}
                      className="h-7 px-2 text-xs"
                      data-testid="tn-add-item-btn"
                    >
                      + Agregar ítem
                    </Button>
                  )}
                </div>

                {!editMode ? (
                  <div className="border border-neutral-200 rounded overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th className="text-left px-2 py-1">Producto</th>
                            <th className="text-right px-2 py-1">Cant.</th>
                            <th className="text-right px-2 py-1">P. Unit</th>
                            <th className="text-right px-2 py-1">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detailVenta.items || []).map((it, i) => (
                            <tr key={i} className="border-t border-neutral-200">
                              <td className="px-2 py-1">
                                {it.name}
                                {it.sku && <span className="ml-2 text-neutral-400 font-mono">SKU:{it.sku}</span>}
                                {it.especificacion && (
                                  <div className="text-[11px] italic text-amber-700 mt-0.5" data-testid={`tn-item-esp-${i}`}>
                                    ↳ {it.especificacion}
                                  </div>
                                )}
                              </td>
                              <td className="text-right px-2 py-1 font-mono">{it.quantity}</td>
                              <td className="text-right px-2 py-1 font-mono">{fmtMoney(it.price, detailVenta.moneda)}</td>
                              <td className="text-right px-2 py-1 font-mono">{fmtMoney(it.subtotal, detailVenta.moneda)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="border border-amber-300 bg-amber-50/30 rounded overflow-hidden" data-testid="tn-edit-items-table">
                    {/* Selector de lista de precios activa (para completar el precio al elegir un producto) */}
                    <div className="flex items-center gap-2 px-2 py-2 border-b border-amber-200 bg-amber-50/60">
                      <div className="text-[10px] uppercase font-mono text-amber-800 tracking-widest">Lista de precios</div>
                      <Select value={editListaPreciosId} onValueChange={setEditListaPreciosId}>
                        <SelectTrigger className="h-7 text-xs w-52" data-testid="tn-edit-lista-precios">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {listasPrecios.map((l) => (
                            <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="text-[10px] text-amber-700/80 flex-1">
                        Al elegir un producto, el precio se autocompleta con esta lista.
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-amber-100/60">
                          <tr>
                            <th className="text-left px-2 py-1 w-[45%]">Producto</th>
                            <th className="text-right px-2 py-1 w-20">Cant.</th>
                            <th className="text-right px-2 py-1 w-28">P. Unit</th>
                            <th className="text-right px-2 py-1">Subtotal</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editItems.map((it, i) => (
                            <tr key={i} className="border-t border-amber-200">
                              <td className="px-2 py-1">
                                <ProductoCombobox
                                  kind="productos"
                                  testid={`tn-edit-item-combo-${i}`}
                                  value={{ codigo: it.sku, nombre: it.name }}
                                  onSelect={(p) => {
                                    const prod = productos.find((x) => x.codigo === p.codigo)
                                              || productos.find((x) => x.nombre === p.nombre);
                                    // BUG FIX: nunca caer silenciosamente a otra lista. Sólo la lista activa
                                    // (Minorista por default en TN). Fallback: precio actual del producto.
                                    let price = null;
                                    if (editListaPreciosId && prod?.precios) {
                                      price = prod.precios[editListaPreciosId];
                                    }
                                    if (!(price && price > 0)) {
                                      const lista = listasPrecios?.find?.((l) => l.id === editListaPreciosId);
                                      if (editListaPreciosId) {
                                        toast.warning(
                                          `"${p.nombre}" no tiene precio en "${lista?.nombre || "la lista seleccionada"}". Se usó precio actual.`,
                                          { duration: 6000 }
                                        );
                                      }
                                    }
                                    updateItem(i, {
                                      name: p.nombre,
                                      sku: p.codigo || "",
                                      tn_product_id: prod?.tn_product_id || it.tn_product_id || null,
                                      price: price && price > 0 ? price : (prod?.precio_actual || p.precio_actual || it.price || 0),
                                    });
                                  }}
                                />
                                <input
                                  type="text"
                                  value={it.sku}
                                  onChange={(e) => updateItem(i, { sku: e.target.value })}
                                  placeholder="SKU (opcional)"
                                  className="w-full mt-1 text-[10px] border border-neutral-200 rounded px-1.5 py-0.5 font-mono text-neutral-500"
                                />
                                <input
                                  type="text"
                                  value={it.especificacion || ""}
                                  onChange={(e) => updateItem(i, { especificacion: e.target.value })}
                                  placeholder="Especificación (ej: cortado en cubos, extra fino…)"
                                  className="w-full mt-1 text-[11px] border border-amber-300 bg-amber-50/60 rounded px-1.5 py-0.5 italic"
                                  data-testid={`tn-edit-item-esp-${i}`}
                                />
                              </td>
                              <td className="px-1 py-1 align-top">
                                <NumberInput raw
                                  min="0"
                                  step="0.001"
                                  value={it.quantity}
                                  onChange={(e) => updateItem(i, { quantity: e.target.value })}
                                  className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono"
                                  data-testid={`tn-edit-item-qty-${i}`}
                                />
                              </td>
                              <td className="px-1 py-1 align-top">
                                <NumberInput raw
                                  min="0"
                                  step="0.01"
                                  value={it.price}
                                  onChange={(e) => updateItem(i, { price: e.target.value })}
                                  className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono"
                                  data-testid={`tn-edit-item-price-${i}`}
                                />
                              </td>
                              <td className="text-right px-2 py-1 font-mono align-top pt-2">
                                {fmtMoney((Number(it.quantity) || 0) * (Number(it.price) || 0), detailVenta.moneda)}
                              </td>
                              <td className="px-1 py-1 align-top">
                                <button
                                  type="button"
                                  onClick={() => removeItem(i)}
                                  className="text-red-500 hover:text-red-700 text-base leading-none"
                                  title="Eliminar ítem"
                                  data-testid={`tn-edit-item-remove-${i}`}
                                >×</button>
                              </td>
                            </tr>
                          ))}
                          {editItems.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-3 text-neutral-400">
                              Sin ítems — usá <strong>+ Agregar ítem</strong>
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {!editMode ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-neutral-500">Subtotal:</span> <span className="font-mono">{fmtMoney(detailVenta.subtotal, detailVenta.moneda)}</span></div>
                    <div><span className="text-neutral-500">Envío:</span> <span className="font-mono">{fmtMoney(detailVenta.envio, detailVenta.moneda)}</span></div>
                    <div><span className="text-neutral-500">Descuento:</span> <span className="font-mono">-{fmtMoney(detailVenta.descuento, detailVenta.moneda)}</span></div>
                    <div><span className="text-neutral-500">Total:</span> <span className="font-mono font-bold">{fmtMoney(detailVenta.total, detailVenta.moneda)}</span></div>
                  </div>

                  {/* Código de descuento manual: se aplica al facturar y activa promos con cupón. */}
                  <div className="border border-neutral-200 rounded p-3 bg-neutral-50/50" data-testid="tn-codigo-promo-panel">
                    <div className="text-xs uppercase text-neutral-500 font-mono mb-2 font-bold">
                      Código de descuento (cupón)
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={detailVenta.codigo_promo || ""}
                        onChange={(e) => setDetailVenta({ ...detailVenta, codigo_promo: e.target.value.toUpperCase() })}
                        placeholder={
                          (detailVenta.cupon_codigos || []).length > 0
                            ? `Sugerido de TN: ${(detailVenta.cupon_codigos || []).join(", ")}`
                            : "Ej: MARZO10"
                        }
                        className="h-9 font-mono uppercase flex-1"
                        data-testid="tn-codigo-promo-input"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="tn-codigo-promo-guardar"
                        onClick={async () => {
                          try {
                            const { data } = await api.patch(
                              `/tiendanube/ventas/${detailVenta.id}/codigo-promo`,
                              { codigo_promo: (detailVenta.codigo_promo || "").trim().toUpperCase() || null },
                            );
                            setDetailVenta(data);
                            toast.success(data.codigo_promo ? `Cupón guardado: ${data.codigo_promo}` : "Cupón eliminado");
                            setVentas((prev) => prev.map((x) => x.id === data.id ? data : x));
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Error al guardar el cupón");
                          }
                        }}
                        className="h-9"
                      >
                        Guardar
                      </Button>
                    </div>
                    <div className="text-[10px] text-neutral-500 mt-2 font-mono">
                      Al facturar este pedido, el cupón se pre-carga automáticamente en el form de la venta y activa las promos con código coincidente.
                    </div>
                  </div>

                  {/* Desglose de descuentos + total pagado */}
                  {(detailVenta.descuento > 0 || detailVenta.total_pagado != null) && (
                    <div className="border border-neutral-200 rounded p-3 bg-neutral-50/50" data-testid="tn-desc-breakdown">
                      {detailVenta.descuento > 0 && (
                        <>
                          <div className="text-xs uppercase text-neutral-500 font-mono mb-2 font-bold">
                            Desglose de descuentos
                          </div>
                          <div className="space-y-1.5 text-xs">
                            {(detailVenta.promociones_aplicadas || []).map((p, i) => (
                              <div key={`p${i}`} className="flex justify-between items-center">
                                <span className="text-neutral-700 flex items-center gap-1">
                                  <span className="inline-block w-1.5 h-1.5 bg-purple-400 rounded-full" />
                                  Promoción: <strong>{p.nombre}</strong>
                                </span>
                                <span className="font-mono text-red-600">-{fmtMoney(p.monto, detailVenta.moneda)}</span>
                              </div>
                            ))}
                            {detailVenta.descuento_cupon > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-neutral-700 flex items-center gap-1">
                                  <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full" />
                                  Cupón{(detailVenta.cupon_codigos || []).length > 0 ? `: ${(detailVenta.cupon_codigos || []).join(", ")}` : ""}
                                </span>
                                <span className="font-mono text-red-600">-{fmtMoney(detailVenta.descuento_cupon, detailVenta.moneda)}</span>
                              </div>
                            )}
                            {detailVenta.descuento_gateway > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-neutral-700 flex items-center gap-1">
                                  <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full" />
                                  Descuento del medio de pago{detailVenta.metodo_pago ? ` (${detailVenta.metodo_pago})` : ""}
                                </span>
                                <span className="font-mono text-red-600">-{fmtMoney(detailVenta.descuento_gateway, detailVenta.moneda)}</span>
                              </div>
                            )}
                            {/* Otros descuentos no desglosados */}
                            {(() => {
                              const desglosado =
                                (detailVenta.promociones_aplicadas || []).reduce((s, p) => s + (p.monto || 0), 0) +
                                (detailVenta.descuento_cupon || 0) +
                                (detailVenta.descuento_gateway || 0);
                              const otros = Math.max(0, (detailVenta.descuento || 0) - desglosado);
                              if (otros > 0.01) {
                                return (
                                  <div className="flex justify-between items-center">
                                    <span className="text-neutral-500 flex items-center gap-1">
                                      <span className="inline-block w-1.5 h-1.5 bg-neutral-400 rounded-full" />
                                      Otros descuentos
                                    </span>
                                    <span className="font-mono text-red-600">-{fmtMoney(otros, detailVenta.moneda)}</span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            <div className="flex justify-between items-center pt-1.5 border-t border-neutral-300 mt-1.5">
                              <span className="text-neutral-800 font-semibold">Total descuentos</span>
                              <span className="font-mono text-red-700 font-bold">-{fmtMoney(detailVenta.descuento, detailVenta.moneda)}</span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Total pagado por el cliente */}
                      {detailVenta.total_pagado != null && (
                        <div className={`${detailVenta.descuento > 0 ? "mt-3 pt-3 border-t border-neutral-300" : ""} flex justify-between items-center`} data-testid="tn-total-pagado">
                          <span className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
                            💵 Total pagado por el cliente
                          </span>
                          <span className="font-mono text-lg font-bold text-emerald-700">
                            {fmtMoney(detailVenta.total_pagado, detailVenta.moneda)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs items-end border border-amber-300 bg-amber-50/30 rounded p-3" data-testid="tn-edit-totals">
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500 font-mono">Subtotal</div>
                    <div className="font-mono text-sm mt-1">{fmtMoney(editSubtotal, detailVenta.moneda)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500 font-mono">Envío</div>
                    <NumberInput raw min="0" step="0.01"
                      value={editEnvio}
                      onChange={(e) => setEditEnvio(e.target.value)}
                      className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono mt-1"
                      data-testid="tn-edit-envio"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500 font-mono">Descuento</div>
                    <NumberInput raw min="0" step="0.01"
                      value={editDescuento}
                      onChange={(e) => setEditDescuento(e.target.value)}
                      className="w-full text-right text-xs border border-neutral-300 rounded px-1.5 py-1 font-mono mt-1"
                      data-testid="tn-edit-descuento"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500 font-mono">Total</div>
                    <div className="font-mono text-base font-bold mt-1" data-testid="tn-edit-total">{fmtMoney(editTotal, detailVenta.moneda)}</div>
                  </div>
                </div>
              )}

              {detailVenta.edited_locally && !editMode && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-2" data-testid="tn-edited-badge">
                  ✎ Este pedido fue modificado localmente en el ERP{detailVenta.edited_at ? ` el ${new Date(detailVenta.edited_at).toLocaleString("es-AR")}` : ""}. Los cambios no se sincronizan con Tienda Nube.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {editMode ? (
              <>
                <Button variant="outline" onClick={exitEditMode} disabled={savingEdit}>Cancelar edición</Button>
                <Button
                  onClick={saveItemsEdit}
                  disabled={savingEdit}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="tn-save-items-btn"
                >
                  {savingEdit ? "Guardando..." : "Guardar cambios"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setDetailVenta(null)}>Cerrar</Button>
                {detailVenta?.anulada_por_nc_numero ? (
                  <Badge
                    variant="outline"
                    className="bg-neutral-200 text-neutral-700 border-neutral-400 h-9 flex items-center px-3"
                    data-testid="tn-detalle-nc-badge"
                    title={`Anulada con NC ${detailVenta.anulada_por_nc_numero}`}
                  >
                    ⊘ NC emitida · N° {detailVenta.anulada_por_nc_numero}
                  </Badge>
                ) : detailVenta?.factura_id ? (
                  <>
                    <Badge
                      variant="outline"
                      className="bg-emerald-50 text-emerald-700 border-emerald-200 h-9 flex items-center px-3"
                      data-testid="tn-detalle-factura-badge"
                    >
                      ✓ Facturado {detailVenta.factura_numero || ""}
                    </Badge>
                    <Button
                      variant="outline"
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                      onClick={() => { setNcTarget(detailVenta); setNcMotivo(""); }}
                      data-testid="tn-detalle-nc-btn"
                    >
                      <XCircle size={14} className="mr-1" /> Generar NC
                    </Button>
                  </>
                ) : (
                  <Button
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    onClick={() => {
                      const id = detailVenta.id;
                      setDetailVenta(null);
                      navigate(`/ventas?tn_venta_id=${id}`);
                    }}
                    data-testid="tn-detalle-facturar-btn"
                  >
                    Facturar pedido →
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: editar templates de WhatsApp */}
      <Dialog open={waEditOpen} onOpenChange={setWaEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="wa-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <WhatsappLogo size={22} weight="fill" className="text-emerald-600" />
              Editar mensajes predeterminados de WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[12px] font-medium text-emerald-800 uppercase tracking-widest">
                  🚚 Envío a domicilio
                </label>
                <div className="flex items-center gap-2">
                  <WhatsAppEmojiPicker
                    onPick={(e) => insertEmojiInto("envio", e)}
                    testId="wa-emoji-picker-envio"
                  />
                  <button
                    type="button"
                    onClick={() => resetWaField("envio")}
                    className="text-[10px] text-neutral-500 hover:text-emerald-700 underline"
                    data-testid="wa-reset-envio"
                  >
                    Restaurar default
                  </button>
                </div>
              </div>
              <textarea
                ref={waEnvioTextareaRef}
                value={waEditForm.envio}
                onChange={(e) => setWaEditForm((f) => ({ ...f, envio: e.target.value }))}
                rows={7}
                data-testid="wa-textarea-envio"
                className="w-full text-[13px] p-3 rounded border border-neutral-300 font-sans resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200"
                placeholder="Mensaje para pedidos con envío a domicilio..."
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[12px] font-medium text-blue-800 uppercase tracking-widest">
                  🏪 Retiro en el local
                </label>
                <div className="flex items-center gap-2">
                  <WhatsAppEmojiPicker
                    onPick={(e) => insertEmojiInto("retiro", e)}
                    testId="wa-emoji-picker-retiro"
                  />
                  <button
                    type="button"
                    onClick={() => resetWaField("retiro")}
                    className="text-[10px] text-neutral-500 hover:text-blue-700 underline"
                    data-testid="wa-reset-retiro"
                  >
                    Restaurar default
                  </button>
                </div>
              </div>
              <textarea
                ref={waRetiroTextareaRef}
                value={waEditForm.retiro}
                onChange={(e) => setWaEditForm((f) => ({ ...f, retiro: e.target.value }))}
                rows={4}
                data-testid="wa-textarea-retiro"
                className="w-full text-[13px] p-3 rounded border border-neutral-300 font-sans resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                placeholder="Mensaje para pedidos con retiro en el local..."
              />
            </div>
            <div className="text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded p-2">
              Estos mensajes se cargan automáticamente al hacer click en el ícono de WhatsApp de cada pedido. Usá el botón <b>😀</b> para insertar emojis 100% compatibles con WhatsApp (Unicode 6-8, sin riesgo de "?").
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaEditOpen(false)} disabled={savingWa}>Cancelar</Button>
            <Button
              onClick={saveWaTemplates}
              disabled={savingWa}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wa-save-btn"
            >
              {savingWa ? "Guardando..." : "Guardar mensajes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Emitir Nota de Crédito */}
      <Dialog open={!!ncTarget} onOpenChange={(o) => { if (!o) { setNcTarget(null); setNcMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="tn-nc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <XCircle size={20} /> Emitir Nota de Crédito
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              Se emitirá una <strong>Nota de Crédito</strong> anulando el comprobante{" "}
              <span className="font-mono font-bold">{ncTarget?.factura_numero || ncTarget?.factura_id}</span>{" "}
              del pedido <span className="font-mono font-bold">#{ncTarget?.tn_number}</span>.
            </p>
            <p className="text-neutral-600 text-xs">
              Si la factura tiene CAE (A/B/C) se emitirá una <strong>NC electrónica en AFIP</strong> con el motivo indicado.
              Si es un Remito / comprobante no fiscal, se generará una <strong>Nota de Crédito X</strong> interna.
              El pedido pasará a estado <strong>Anulado</strong> y los cobros aplicados serán revertidos.
            </p>
            <div>
              <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest mb-1">Motivo (opcional, solo para NC electrónica)</div>
              <Input
                value={ncMotivo}
                onChange={(e) => setNcMotivo(e.target.value)}
                placeholder="Ej: Cliente devolvió mercadería"
                data-testid="tn-nc-motivo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNcTarget(null); setNcMotivo(""); }} disabled={ncSubmitting}>
              Cancelar
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={emitirNC}
              disabled={ncSubmitting}
              data-testid="tn-nc-emitir-btn"
            >
              {ncSubmitting ? "Emitiendo NC..." : "Emitir Nota de Crédito"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SyncPreciosTNDialog open={syncPreciosOpen} onOpenChange={setSyncPreciosOpen} />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, colorClass = "text-neutral-800", active = false, onClick }) {
  const cls = onClick ? "cursor-pointer transition-all hover:border-neutral-500 hover:shadow-sm" : "";
  const activeCls = active ? "border-neutral-800 ring-2 ring-neutral-200 shadow" : "";
  return (
    <div className={`border border-neutral-300 rounded bg-white px-4 py-3 flex items-center gap-3 ${cls} ${activeCls}`}
      onClick={onClick}
      data-testid={onClick && label ? `stat-${label.replace(/\s+/g, "-").toLowerCase()}` : undefined}
    >
      <Icon size={22} weight="regular" className={colorClass} />
      <div>
        <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{label}</div>
        <div className={`text-xl font-bold ${colorClass}`}>{value}</div>
      </div>
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
